import type {
    ChallengeRuleConfigDto,
    ChallengeTeamRuleSetDto
} from '@core/api/ChallengeClient';
import { AggregationAlgorithm } from '@core/api/ChallengeClient';
import { autodisposer } from '@core/reactions';
import { injectTSDI } from '@core/tsdi';
import { FieldState, FormState } from 'formstate';
import { computed, makeObservable, observable, reaction } from 'mobx';
import { component } from 'tsdi';
import { ChallengeDataStore } from '../../data-store';
import {
    formErrors,
    isFormStateDirty,
    makeFormClean
} from '@components/utils/form';
import { wrapInFormField } from '@core/forms';
import { required } from '@components/utils/validators';

import { bind } from 'lodash-decorators';
import { StepStatus } from '../../store';

export type ChallengeTeamRulesFormType = ReturnType<
    TeamRulesStore['generateForm']
>;

export const DEFAULT_TEAM_RULE_SET: ChallengeTeamRuleSetDto = {
    leaderboardEnabled: true,
    pointsAlgorithm: AggregationAlgorithm.AVERAGE,
    winnersCount: 3,
    minimumTeamSize: 5
};

@component
export class TeamRulesStore {
    constructor() {
        this.form = this.generateForm(
            this.challengeDataStore.challengeConfiguration?.rule
        );

        makeObservable(this, {
            form: observable,
            fields: computed,
            errors: computed
        });
    }

    public form: ChallengeTeamRulesFormType;
    public state: StepStatus = StepStatus.INCOMPLETE;

    private get challengeDataStore() {
        return injectTSDI(ChallengeDataStore);
    }

    public generateForm(configuration?: ChallengeRuleConfigDto) {
        return new FormState({
            teamRulesEnabled: new FieldState<boolean>(
                Boolean(configuration?.teamRuleSet) || false
            ),
            leaderboardEnabled: new FieldState<boolean>(
                Boolean(configuration?.teamRuleSet?.leaderboardEnabled) || true
            ),

            pointsAlgorithm: new FieldState<AggregationAlgorithm>(
                configuration?.teamRuleSet?.pointsAlgorithm ||
                    DEFAULT_TEAM_RULE_SET.pointsAlgorithm
            ).validators(required()),
            winnersCount: new FieldState<number | undefined>(
                configuration?.teamRuleSet?.winnersCount ??
                    DEFAULT_TEAM_RULE_SET.winnersCount
            ).validators((val) => this.winnersCountValidator(val)),
            minimumTeamSize: new FieldState<number | undefined>(
                configuration?.teamRuleSet?.minimumTeamSize ??
                    DEFAULT_TEAM_RULE_SET.minimumTeamSize
            ).validators((val) => this.minimumTeamSizeValidator(val))
        });
    }

    @bind
    private winnersCountValidator(val: number | undefined): string | undefined {
        if (!val) {
            return 'Number of winners must be at least 1';
        }
        return undefined;
    }
    @bind
    private minimumTeamSizeValidator(
        val: number | undefined
    ): string | undefined {
        if (!val) {
            return 'Minimum team size must be at least 1';
        }
        return undefined;
    }

    public get fields() {
        return {
            teamRulesEnabled: wrapInFormField(this.form.$.teamRulesEnabled),
            leaderboardEnabled: wrapInFormField(this.form.$.leaderboardEnabled),
            pointsAlgorithm: wrapInFormField(this.form.$.pointsAlgorithm),
            winnersCount: wrapInFormField(this.form.$.winnersCount),
            minimumTeamSize: wrapInFormField(this.form.$.minimumTeamSize)
        };
    }

    public get errors() {
        return formErrors(this.form);
    }

    public generateRequestData(
        form: ChallengeTeamRulesFormType
    ): ChallengeTeamRuleSetDto {
        const {
            $: {
                leaderboardEnabled,
                pointsAlgorithm,
                winnersCount,
                minimumTeamSize
            }
        } = form;

        return {
            leaderboardEnabled: leaderboardEnabled.value,
            pointsAlgorithm: pointsAlgorithm.value,
            winnersCount: winnersCount.value,
            minimumTeamSize: minimumTeamSize.value
        };
    }

    public get formIsDirty() {
        return isFormStateDirty(this.form.$);
    }

    @bind
    public async onChangeHandle() {
        const validation = await this.form.validate();
        if (validation.hasError) {
            this.state = StepStatus.ERROR;
        } else {
            this.state = StepStatus.MODIFIED;
        }
    }

    @bind
    public async updateForm(configuration?: ChallengeRuleConfigDto) {
        this.form = this.generateForm(configuration);
        const validation = await this.form.validate();
        if (validation.hasError) {
            this.state = StepStatus.ERROR;
            return;
        }
        this.state = StepStatus.COMPLETED;
        makeFormClean(this.form);
        return;
    }

    @autodisposer.tsdi
    public initialize() {
        return [
            reaction(
                () => this.challengeDataStore.challengeConfiguration,
                async (configuration) => {
                    this.updateForm(configuration?.rule);
                }
            )
        ];
    }
}
